import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink';

import {
  ImpersonationActions,
  ImpersonationSelectors,
} from '@app/easylot-admin/modules/impersonation/store/impersonation.slice';
import { UserDetails } from '@authentication/interfaces/user';
import { UserService } from '@authentication/services/user.service';

import { DiagnosticDeepDebugSettingsComponent } from '../../../../diagnostics/components/diagnostic-deep-debug-settings/diagnostic-deep-debug-settings.component';
import { DiagnosticProfile } from '../../../../diagnostics/models/diagnostic-profile';
import { DiagnosticBufferService } from '../../../../diagnostics/services/diagnostic-buffer.service';
import { DiagnosticProfilePreferenceService } from '../../../../diagnostics/services/diagnostic-profile-preference.service';
import { DiagnosticProfileService } from '../../../../diagnostics/services/diagnostic-profile.service';

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
})
export class UserMenuComponent implements OnInit, OnDestroy {
  user?: UserDetails;
  monogram?: string;
  roles: Array<string> = [];
  subs = new SubSink();

  impersonated$ = this.store.select(
    ImpersonationSelectors.selectImpersonatedMerchantId
  );
  // Boolean-shaped variant of impersonated$ — `=== false` is the only
  // lint-accepted way to express "not impersonating" in templates
  // (no-negated-async), while impersonated$ itself is `string` ('' = none).
  hasImpersonation$ = this.impersonated$.pipe(map((id) => !!id));

  availableProfiles: DiagnosticProfile[] = [];
  activeProfile?: DiagnosticProfile;
  bufferSizeKb$ = new BehaviorSubject<number>(0);

  get hasDeepDebug(): boolean {
    return this.availableProfiles.some((p) => p.name === 'deepDebug');
  }

  constructor(
    private userService: UserService,
    private dialog: MatDialog,
    private store: Store,
    private diagnosticProfileService: DiagnosticProfileService,
    private diagnosticBuffer: DiagnosticBufferService,
    private diagnosticPreference: DiagnosticProfilePreferenceService
  ) {
    this.subs.sink = this.userService
      .getUser()
      .subscribe((user: UserDetails) => {
        this.roles = user.roles;
        this.user = user;
        this.monogram = this.user
          ? `${this.user.familyName[0]} ${this.user.givenName[0]}`
          : '';
      });
  }

  ngOnInit(): void {
    this.activeProfile = this.diagnosticBuffer.getActiveProfile();
    this.subs.sink = this.diagnosticProfileService
      .getAvailableProfiles()
      .subscribe((profiles) => {
        this.availableProfiles = profiles;
      });
  }

  onSelectProfile(profile: DiagnosticProfile): void {
    this.diagnosticBuffer.setActiveProfile(profile);
    this.diagnosticPreference.write(profile.name);
    this.activeProfile = profile;
  }

  openDeepDebugSettings(): void {
    this.dialog.open(DiagnosticDeepDebugSettingsComponent, {
      autoFocus: false,
      restoreFocus: true,
    });
  }

  /**
   * Called when the diagnostic submenu opens. Computes the buffer size once
   * at open time — no interval polling, to avoid a potentially expensive
   * JSON.stringify on large deepDebug buffers every 5 seconds.
   * The value is always fresh when the user sees it.
   */
  onDiagnosticSubmenuOpened(): void {
    this.bufferSizeKb$.next(
      Math.round(this.diagnosticBuffer.sizeBytes() / 1024)
    );
  }

  removeImpersonation() {
    this.store.dispatch(ImpersonationActions.removeImpersonation.trigger({}));
    this.userService.resetStores();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
